import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FaCalculator, FaCheck, FaTruck } from "react-icons/fa";
import {
  SIMULACAO_DEFAULTS,
  calcPrecoFinalSimulacao,
} from "../utils/simulacao-custos";

const API_PRECOS = "/api/frota/historico-precos";

/** KM ida/volta tabelados (mesma tabela da Simulação de Custos) */
const KM_TABELADOS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,0000";
  return `R$ ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

function isoToBR(iso) {
  if (!iso) return "–";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/**
 * Painel flutuante de simulação de custos (mesma lógica de /simulacao-custos).
 * Aplica o preço final no produto selecionado do carrinho.
 */
const SimulacaoCustoFlutuante = ({
  isOpen,
  onClose,
  produto = null,
  onAplicar,
}) => {
  const toast = useToast();
  const [dataPreco, setDataPreco] = useState(todayISO());
  const [precoCusto, setPrecoCusto] = useState(0);
  const [loadingPreco, setLoadingPreco] = useState(false);
  const [precoAviso, setPrecoAviso] = useState("");

  const [kilometragem, setKilometragem] = useState(300);
  const [tipoVeiculo, setTipoVeiculo] = useState("toco");
  const [margem, setMargem] = useState(SIMULACAO_DEFAULTS.margem);
  const [maoDeObra, setMaoDeObra] = useState(SIMULACAO_DEFAULTS.maoDeObra);
  const [pneu, setPneu] = useState(SIMULACAO_DEFAULTS.pneu);
  const [manutencao, setManutencao] = useState(SIMULACAO_DEFAULTS.manutencao);
  const [custoFixo, setCustoFixo] = useState(SIMULACAO_DEFAULTS.custoFixo);

  useEffect(() => {
    if (!isOpen || !produto) return;
    const dataInicial =
      String(produto.dataPreco || produto.dataPrecoRef || todayISO()).slice(
        0,
        10,
      );
    setDataPreco(dataInicial);
    setPrecoCusto(
      Number(produto.precoCusto ?? produto.precoUnitario) || 0,
    );
    setPrecoAviso("");

    if (produto.simulacao?.kilometragem != null) {
      const kmSalvo = Number(produto.simulacao.kilometragem);
      setKilometragem(
        KM_TABELADOS.includes(kmSalvo) ? kmSalvo : KM_TABELADOS[2],
      );
    } else {
      setKilometragem(KM_TABELADOS[2]);
    }
    if (produto.simulacao?.tipoVeiculo) {
      setTipoVeiculo(produto.simulacao.tipoVeiculo);
    }
    if (produto.simulacao?.margem != null) {
      setMargem(produto.simulacao.margem);
    }
  }, [isOpen, produto]);

  useEffect(() => {
    if (!isOpen || !produto?.codigo) return;

    let cancelled = false;
    const buscarPreco = async () => {
      setLoadingPreco(true);
      setPrecoAviso("");
      try {
        const res = await fetch(
          `${API_PRECOS}?de=${encodeURIComponent(dataPreco)}&ate=${encodeURIComponent(dataPreco)}`,
        );
        if (!res.ok) throw new Error("Falha ao buscar preço");
        const rows = await res.json();
        const list = Array.isArray(rows) ? rows : [];
        const cod = String(produto.codigo);
        const fornId = produto.fornecedorId
          ? String(produto.fornecedorId)
          : null;

        const candidatos = list.filter((row) => {
          if (String(row.produto_id) !== cod) return false;
          if (fornId && String(row.fornecedor_id) !== fornId) return false;
          return String(row.data || "").slice(0, 10) === dataPreco;
        });

        if (candidatos.length === 0) {
          if (!cancelled) {
            setPrecoAviso(
              `Nenhum preço encontrado para ${isoToBR(dataPreco)}.`,
            );
          }
          return;
        }

        candidatos.sort((a, b) =>
          String(b.created_at || "").localeCompare(String(a.created_at || "")),
        );
        const valor = Number(candidatos[0].valor) || 0;
        if (!cancelled) {
          setPrecoCusto(valor);
          setPrecoAviso("");
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPrecoAviso("Não foi possível carregar o preço desta data.");
          toast({
            title: "Erro ao buscar preço",
            description: e.message,
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      } finally {
        if (!cancelled) setLoadingPreco(false);
      }
    };

    buscarPreco();
    return () => {
      cancelled = true;
    };
  }, [isOpen, produto?.codigo, produto?.fornecedorId, dataPreco, toast]);

  const resultado = useMemo(
    () =>
      calcPrecoFinalSimulacao({
        precoCusto,
        kilometragem,
        tipoVeiculo,
        margem,
        maoDeObra,
        pneu,
        manutencao,
        custoFixo,
      }),
    [
      precoCusto,
      kilometragem,
      tipoVeiculo,
      margem,
      maoDeObra,
      pneu,
      manutencao,
      custoFixo,
    ],
  );

  const jaNoCarrinho =
    produto?.cartIndex != null || Boolean(produto?.cartKey);

  const handleAplicar = () => {
    onAplicar?.({
      precoFinal: resultado.precoFinal,
      precoCusto: resultado.precoCusto,
      dataPreco,
      simulacao: {
        ...resultado,
        dataPreco,
        produtoKey: produto?.key || produto?.cartKey || null,
        produtoCodigo: produto?.codigo || null,
        produtoDesc: produto?.descricao || "",
        fornecedor: produto?.fornecedor || "",
      },
    });
    onClose?.();
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      size="md"
      closeOnOverlayClick
    >
      <DrawerOverlay bg="blackAlpha.400" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" bg="#007abe" color="white" pr={12}>
          <HStack spacing={2}>
            <Icon as={FaCalculator} />
            <Box>
              <Text fontSize="md" fontWeight="bold">
                Simulação de custos
              </Text>
              <Text fontSize="xs" fontWeight="normal" opacity={0.9}>
                Mesma lógica de Simulação de Custos de Transporte
              </Text>
            </Box>
          </HStack>
        </DrawerHeader>

        <DrawerBody py={5}>
          {!produto ? (
            <Text color="gray.500">Nenhum produto selecionado.</Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Box
                p={3}
                bg="blue.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.100"
              >
                <Text fontSize="xs" color="#007abe" fontWeight="semibold">
                  Produto selecionado
                </Text>
                <Text fontWeight="bold" color="gray.800">
                  {produto.descricao}
                </Text>
                {produto.fornecedor && (
                  <Text fontSize="sm" color="gray.600">
                    {produto.fornecedor}
                  </Text>
                )}
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="gray">
                    Custo: {formatMoney(precoCusto)}
                  </Badge>
                  <Badge colorScheme="green">
                    Final: {formatMoney(resultado.precoFinal)}
                  </Badge>
                  <Badge colorScheme="blue">
                    Data: {isoToBR(dataPreco)}
                  </Badge>
                </HStack>
              </Box>

              <FormControl>
                <FormLabel fontSize="xs" color="#007abe">
                  Data do preço (custo)
                </FormLabel>
                <HStack>
                  <Input
                    size="sm"
                    type="date"
                    value={dataPreco}
                    max={todayISO()}
                    onChange={(e) =>
                      setDataPreco(e.target.value || todayISO())
                    }
                  />
                  {loadingPreco && <Spinner size="sm" color="#007abe" />}
                </HStack>
                {precoAviso && (
                  <Text fontSize="xs" color="orange.600" mt={1}>
                    {precoAviso}
                  </Text>
                )}
              </FormControl>

              <SimpleGrid columns={2} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" color="#007abe">
                    KM (ida/volta)
                  </FormLabel>
                  <Select
                    size="sm"
                    value={String(kilometragem)}
                    onChange={(e) =>
                      setKilometragem(Number(e.target.value) || KM_TABELADOS[0])
                    }
                  >
                    {KM_TABELADOS.map((km) => (
                      <option key={km} value={km}>
                        {km} km
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color="#007abe">
                    <HStack spacing={1}>
                      <Icon as={FaTruck} boxSize={3} />
                      <Text>Tipo veículo</Text>
                    </HStack>
                  </FormLabel>
                  <Select
                    size="sm"
                    value={tipoVeiculo}
                    onChange={(e) => setTipoVeiculo(e.target.value)}
                  >
                    <option value="toco">Toco (rural)</option>
                    <option value="truck">Truck (cidade)</option>
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="xs" color="#007abe">
                  Margem (R$/L)
                </FormLabel>
                <NumberInput
                  size="sm"
                  min={0}
                  step={0.01}
                  precision={4}
                  value={margem}
                  onChange={(v) => setMargem(parseFloat(v) || 0)}
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>

              <Divider />
              <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                Parâmetros de custo (editáveis)
              </Text>
              <SimpleGrid columns={2} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs">Mão de obra</FormLabel>
                  <NumberInput
                    size="sm"
                    min={0}
                    precision={4}
                    value={maoDeObra}
                    onChange={(v) => setMaoDeObra(parseFloat(v) || 0)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Pneu</FormLabel>
                  <NumberInput
                    size="sm"
                    min={0}
                    precision={4}
                    value={pneu}
                    onChange={(v) => setPneu(parseFloat(v) || 0)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Manutenção</FormLabel>
                  <NumberInput
                    size="sm"
                    min={0}
                    precision={4}
                    value={manutencao}
                    onChange={(v) => setManutencao(parseFloat(v) || 0)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs">Custo fixo / L</FormLabel>
                  <NumberInput
                    size="sm"
                    min={0}
                    precision={4}
                    value={custoFixo}
                    onChange={(v) => setCustoFixo(parseFloat(v) || 0)}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </SimpleGrid>

              <Box
                p={4}
                bg="gray.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="xs" fontWeight="bold" color="#007abe" mb={2}>
                  Detalhamento
                </Text>
                <VStack align="stretch" spacing={1} fontSize="sm">
                  <Flex justify="space-between">
                    <Text color="gray.600">Preço de custo</Text>
                    <Text fontWeight="medium">
                      {formatMoney(resultado.precoCusto)}
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">Média consumo (custo/4)</Text>
                    <Text>{resultado.mediaConsumo.toFixed(4)}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">Custo variável / km</Text>
                    <Text>{resultado.custoVariavel.toFixed(4)}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">Frete / L</Text>
                    <Text>{formatMoney(resultado.fretePorLitro)}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">Margem</Text>
                    <Text>{formatMoney(resultado.margem)}</Text>
                  </Flex>
                  <Divider my={1} />
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="bold" color="gray.800">
                      Preço final
                    </Text>
                    <Text fontWeight="bold" fontSize="lg" color="green.600">
                      {formatMoney(resultado.precoFinal)}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.500">
                    = custo + frete ({tipoVeiculo === "truck" ? "truck" : "toco"}{" "}
                    · {resultado.kilometragem} km) + margem
                  </Text>
                </VStack>
              </Box>
            </VStack>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" gap={2}>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            leftIcon={<FaCheck />}
            bg="#007abe"
            color="white"
            _hover={{ bg: "#0066a3" }}
            onClick={handleAplicar}
            isDisabled={
              !produto || !(resultado.precoFinal > 0) || loadingPreco
            }
            isLoading={loadingPreco}
          >
            {jaNoCarrinho
              ? "Atualizar preço no carrinho"
              : "Aplicar e adicionar ao carrinho"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SimulacaoCustoFlutuante;
